import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import { styled, useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { Autocomplete, Box, MenuItem, TextField } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { getSelectedNodeRedux } from '../../../../redux/selectedNodeSlice';
import { getIAAM } from '../../../../redux/iaamSlice';
// import { useDataGridPremiumProps } from '@mui/x-data-grid-premium/DataGridPremium/useDataGridPremiumProps';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

function SelectResourceDialog(props) {

    const theme = useTheme();
    const { open, setOpen, setNodes, setUpdateProperties } = props;
    const hasInitializedSelection = useRef(false);
    const [disabled, setDisabled] = useState(true);
    const [dpes, setDpes] = useState([]);
    const [dpeSelected, setDpeSelected] = useState(null);
    const [resources, setResources] = useState([]);
    const [resourceSelected, setResourceSelected] = useState();
    const [project, setProject] = useState();
    const [dpeError, setDpeError] = useState(false);
    const [dpeErrorText, setDpeErrorText] = useState("");

    const iaam = useSelector(getIAAM);
    const selectedNode = useSelector(getSelectedNodeRedux);

    useEffect(() => {
        const project = JSON.parse(localStorage.getItem('project'));
        if (project) {
            setProject(project);
        }
    }, []);

    const handleClose = () => {
        hasInitializedSelection.current = false;
        setDpes([]);
        setResources([]);
        setDpeSelected(null);
        setResourceSelected("");
        setOpen(false);
    };

    const handleSelect = async () => {
        const accessToken = await iaam.getAccessToken();
        let option = {
            method: 'GET',
            headers: {
                'Content-type': 'application/json',
                'Authorization': `bearer ${accessToken}`
            }
        }
        const response = await fetch(`${dpeSelected?.resource_schema_endpoint}?resource=${resourceSelected}`, option);
        if (response.ok) {
            const result = await response.json();
            const sort_attr = result.sort((a, b) => a.column_name.localeCompare(b.column_name));
            const new_result = sort_attr.map((attr) => {
                return {
                    name: attr.column_name,
                    data_type: attr.data_type,
                    checked: false,
                    // used: false
                };
            });
            setNodes((nds) => nds.map((node) => {
                if (node.id === selectedNode.id) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            error_dpe: false,
                            error_dpe_text: [],
                            general_properties: {
                                ...node.data.general_properties,
                                dpe: dpeSelected,
                                resource: resourceSelected,
                                resource_schema: new_result
                            }
                        }
                    };
                }
                return node;
            }));
            handleClose();
            setUpdateProperties(true);
        } else {
            console.error(response);
        }
    };

    const fetchDpe = useCallback(async () => {
        if (!project?.project_id) {
            setDpes([]);
            return;
        }

        let DPEs = [];
        const response = await fetch(`${process.env.REACT_APP_API_URL}/dpe/get-dpes/${project.project_id}`);
        if (response.ok) {
            const dicovery = await response.json();
            for (const dLink of dicovery) {
                const dpe_resp = await fetch(dLink.dpe_discovery_url);
                if (dpe_resp.ok) {
                    const dpe = await dpe_resp.json();
                    DPEs.push({
                        dpe_id: dLink.dpe_id,
                        ...dpe
                    })
                }
                else {
                    DPEs.push({
                        dpe_id: dLink.dpe_id,
                        resources_name: dLink.dpe_name,
                        resource_type: "DB_Null",
                        resource_connection: {
                            status: "Error",
                            message: "connection refused!"
                        }
                    })
                }
            }
        }
        setDpes(DPEs);
    }, [project]);

    const fetchResources = useCallback(async (selectedDpe) => {
        if (!selectedDpe?.resources_endpoint) {
            setResources([]);
            return;
        }

        const accessToken = await iaam.getAccessToken();
        let option = {
            method: 'GET',
            headers: {
                'Content-type': 'application/json',
                'Authorization': `bearer ${accessToken}`
            }
        }
        const response = await fetch(`${selectedDpe.resources_endpoint}`, option);
        if (response.ok) {
            const result = await response.json();
            setResources(result);
        } else {
            setResources([]);
            console.error(response);
        }
    }, [iaam]);

    const handleChangeDPE = (newChange) => {
        if (!newChange) {
            setDpeSelected(null);
            setResourceSelected("");
            setResources([]);
            setDpeError(false);
            setDpeErrorText("");
            return;
        }

        if (dpeSelected?.dpe_id !== newChange.dpe_id) {
            setResourceSelected("");
            setDpeError(false);
            setDpeErrorText("");
        }
        setDpeSelected(newChange);
    };

    useEffect(() => {
        if (dpeSelected) {
            fetchResources(dpeSelected);
        } else {
            setResources([]);
        }
    }, [dpeSelected, fetchResources]);

    useEffect(() => {
        if (!open) {
            hasInitializedSelection.current = false;
            return;
        }

        if (!selectedNode || hasInitializedSelection.current) {
            return;
        }

        hasInitializedSelection.current = true;
        fetchDpe();

        if (selectedNode?.type === 'root') {
            setDpeSelected(selectedNode.data.general_properties.dpe ?? null);
            setResourceSelected(selectedNode.data.general_properties.resource ?? "");
            if (selectedNode?.data?.error_dpe) {
                setDpeError(true);
                setDpeErrorText(selectedNode?.data?.error_dpe_text[0]);
                setDpeSelected(null);
                setResourceSelected("");
            } else {
                setDpeError(false);
                setDpeErrorText("");
            }
        }
    }, [open, selectedNode, fetchDpe]);

    useEffect(() => {
        if (
            (dpeSelected?.dpe_id !== selectedNode?.data?.general_properties?.dpe?.dpe_id || resourceSelected !== selectedNode?.data?.general_properties?.resource)
            && (dpeSelected && resourceSelected)
        ) {
            setDisabled(false);
        } else {
            setDisabled(true);
        }
    }, [dpeSelected, resourceSelected, selectedNode]);

    return (
        <Fragment>
            <BootstrapDialog
                aria-labelledby="customized-dialog-title"
                fullWidth
                maxWidth="xs"
                open={open}
            >
                <DialogTitle
                    // sx={{ m: 0, p: 2, backgroundColor: theme.palette.primary.main, borderTopLeftRadius: 2, borderTopRightRadius: 2 }} 
                    // id="customized-dialog-title"
                    sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.white.main, paddingY: 1, paddingX: 2 }}
                    className='flex justify-between items-center'
                >
                    <div>
                        <Typography color={theme.palette.white.main}>Select Resource</Typography>
                    </div>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            padding: 0,
                            color: theme.palette.white.main
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ paddingX: 3, paddingY: 2 }}>
                        <TextField
                            id="data-proxy-engine"
                            sx={{ color: '#000' }}
                            fullWidth
                            select
                            margin="normal"
                            label="Data Proxy Engine"
                            value={dpeSelected?.dpe_id || ''}
                            error={dpeError}
                            helperText={dpeErrorText}
                            onChange={(e) => {
                                const selectedDpeId = e.target.value;
                                const selectedDPEObject = dpes.find((dpe) => String(dpe.dpe_id) === String(selectedDpeId));
                                handleChangeDPE(selectedDPEObject);
                            }}
                        >
                            {dpes.map((option) => (
                                <MenuItem key={option.dpe_id} value={option.dpe_id}>
                                    {option.resources_name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Autocomplete
                            fullWidth
                            id="resource"
                            // disabled={disableResource}
                            options={resources}
                            value={resourceSelected || null}
                            onChange={(event, newValue) => {
                                setResourceSelected(newValue || "");
                            }}
                            getOptionLabel={(option) => option}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Resource"
                                    margin="normal"
                                />
                            )}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Box sx={{ m: 0, paddingX: 1, paddingY: 1 }}>
                        <Button variant="contained" disabled={disabled} onClick={() => handleSelect()}>Select</Button>
                    </Box>
                </DialogActions>
            </BootstrapDialog>
        </Fragment>
    )
}

export default SelectResourceDialog